import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Input, Space, Tag, Typography } from 'antd';
import { EpcEstimatingBoqGrid } from './EpcEstimatingBoqGrid';
import type { BoqRollup } from './EpcEstimatingBoqGrid';
import { EpcEstimatingWorkbenchInspector } from './EpcEstimatingWorkbenchInspector';
import { isPhase2HealthCode } from './costingMethodHelpers';
import styles from './EpcEstimatingWorkbench.module.css';

const { Text } = Typography;

export interface HealthIssue {
  code?: string;
  severity?: string;
  message?: string;
  client_boq_line_id?: string;
  boq_line_id?: string;
  entity_id?: string;
}

export interface EpcEstimatingWorkbenchSurfaceProps {
  estimateId: string;
  revisionId: string;
  boqLines: any[];
  costLines: any[];
  wbsNodes?: any[];
  boqLoading?: boolean;
  readOnly?: boolean;
  selectedBoqLineId: string | null;
  onSelectBoqLine: (id: string | null) => void;
  boqRollups?: Map<string, BoqRollup>;
  coverageFilter?: 'all' | 'without_cost' | 'unlinked';
  onCoverageFilterChange?: (f: 'all' | 'without_cost' | 'unlinked') => void;
  onBatchSave: React.ComponentProps<typeof EpcEstimatingBoqGrid>['onBatchSave'];
  savePending?: boolean;
  onAddCost?: (boqLineId: string) => void;
  onOpenCostDetail?: (line: Record<string, unknown>) => void;
  onOpenBuildUp?: (line: Record<string, unknown>) => void;
  /** After COSTING mutations — reload BOQ / cost / diagnostics. */
  onMutated?: () => void;
  healthIssues?: HealthIssue[];
  jumpToId?: string | null;
  onJumpConsumed?: () => void;
}

export const EpcEstimatingWorkbenchSurface: React.FC<EpcEstimatingWorkbenchSurfaceProps> = ({
  estimateId,
  revisionId,
  boqLines,
  costLines,
  wbsNodes,
  boqLoading,
  readOnly,
  selectedBoqLineId,
  onSelectBoqLine,
  boqRollups,
  coverageFilter,
  onCoverageFilterChange,
  onBatchSave,
  savePending,
  onAddCost,
  onOpenCostDetail,
  onOpenBuildUp,
  onMutated,
  healthIssues,
  jumpToId,
}) => {
  const [isCompact, setIsCompact] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1024px)').matches : false
  );
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [navFilter, setNavFilter] = useState('');
  const [healthOpen, setHealthOpen] = useState(false);
  const [activeJump, setActiveJump] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 1024px)');
    const onChange = () => setIsCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (selectedBoqLineId && isCompact) setInspectorOpen(true);
  }, [selectedBoqLineId, isCompact]);

  const healthLineIds = useMemo(() => {
    const s = new Set<string>();
    for (const h of healthIssues || []) {
      const id = h.client_boq_line_id || h.boq_line_id || h.entity_id;
      if (id) s.add(String(id));
    }
    return s;
  }, [healthIssues]);

  const errorCount = (healthIssues || []).filter(
    (h) => h.severity === 'error' || h.severity === 'BLOCKER'
  ).length;
  const warnCount = (healthIssues || []).filter(
    (h) => h.severity === 'warn' || h.severity === 'WARNING'
  ).length;

  const navRows = useMemo(() => {
    const q = navFilter.trim().toLowerCase();
    const wbs = (wbsNodes || []).slice(0, 200);
    const boq = (boqLines || []).filter((r) => !(r.parent_id || r.parentId)).slice(0, 80);
    return {
      wbs,
      boq: q
        ? boq.filter((b) => `${b.item_number} ${b.description}`.toLowerCase().includes(q))
        : boq,
    };
  }, [wbsNodes, boqLines, navFilter]);

  const jump = jumpToId || activeJump;

  const healthChip = (
    <Tag
      className={styles.healthChip}
      color={errorCount ? 'red' : warnCount ? 'orange' : 'green'}
      onClick={() => setHealthOpen(true)}
      data-testid="estimating-health-chip"
    >
      Health · {errorCount} / {warnCount}
    </Tag>
  );

  const inspector = (
    <EpcEstimatingWorkbenchInspector
      estimateId={estimateId}
      revisionId={revisionId}
      selectedBoqLineId={selectedBoqLineId}
      costLines={costLines}
      readOnly={readOnly}
      onAddCost={onAddCost}
      onOpenCostDetail={onOpenCostDetail}
      onOpenBuildUp={onOpenBuildUp}
      onMutated={onMutated}
      healthIssues={healthIssues}
    />
  );

  return (
    <div className={styles.workbenchRoot} data-testid="estimating-workbench">
      <div className={styles.workbenchBody}>
        <nav className={styles.navigator} aria-label="WBS and BOQ navigator">
          <div className={styles.navigatorHeader}>Navigator</div>
          <div style={{ padding: '6px 8px' }}>
            <Input.Search
              size="small"
              allowClear
              placeholder="Filter"
              onSearch={setNavFilter}
              onChange={(e) => {
                if (!e.target.value) setNavFilter('');
              }}
            />
          </div>
          <div className={styles.navigatorScroll}>
            <div className={styles.navigatorHeader}>WBS</div>
            {(navRows.wbs || []).map((n: any) => (
              <div key={n.id} className={styles.navItem} title={n.name || n.code}>
                {n.code || n.name}
              </div>
            ))}
            <div className={styles.navigatorHeader}>BOQ (roots)</div>
            {navRows.boq.map((b: any) => (
              <div
                key={b.id}
                className={[
                  styles.navItem,
                  selectedBoqLineId === b.id ? styles.navItemActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  onSelectBoqLine(b.id);
                  setActiveJump(b.id);
                }}
              >
                {b.item_number || b.description}
              </div>
            ))}
          </div>
        </nav>

        <EpcEstimatingBoqGrid
          lines={boqLines}
          loading={boqLoading}
          readOnly={readOnly}
          selectedId={selectedBoqLineId}
          onSelect={(id) => {
            onSelectBoqLine(id);
            setActiveJump(null);
          }}
          boqRollups={boqRollups}
          coverageFilter={coverageFilter}
          onCoverageFilterChange={onCoverageFilterChange}
          healthLineIds={healthLineIds}
          onBatchSave={onBatchSave}
          savePending={savePending}
          jumpToId={jump}
          toolbarExtra={
            <Space size={6} wrap>
              {healthChip}
              {isCompact ? (
                <Button
                  size="small"
                  type={selectedBoqLineId ? 'primary' : 'default'}
                  className={styles.inspectorToggle}
                  data-testid="estimating-costing-toggle"
                  onClick={() => setInspectorOpen(true)}
                >
                  Costing Inspector
                </Button>
              ) : null}
            </Space>
          }
        />

        <div className={styles.inspectorDocked}>{inspector}</div>
      </div>

      <Drawer
        title="Costing Inspector"
        open={isCompact && inspectorOpen}
        onClose={() => setInspectorOpen(false)}
        width={Math.min(420, typeof window !== 'undefined' ? window.innerWidth - 24 : 420)}
        destroyOnClose={false}
        styles={{ body: { padding: 0, height: '100%' } }}
      >
        <div className={styles.inspectorDrawerBody}>{inspector}</div>
      </Drawer>

      <Drawer
        title="Estimate Health"
        open={healthOpen}
        onClose={() => setHealthOpen(false)}
        width={420}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {(healthIssues || []).length === 0 ? (
            <Text type="secondary">No diagnostics issues reported.</Text>
          ) : (
            (healthIssues || []).map((h, i) => {
              const target = h.client_boq_line_id || h.boq_line_id || h.entity_id;
              return (
                <div
                  key={`${h.code}-${i}`}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                    padding: 8,
                    cursor: target ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (!target) return;
                    onSelectBoqLine(String(target));
                    setActiveJump(String(target));
                    setHealthOpen(false);
                    if (isCompact) setInspectorOpen(true);
                  }}
                >
                  <Space wrap size={4}>
                    <Tag
                      color={
                        h.severity === 'error' || h.severity === 'BLOCKER'
                          ? 'red'
                          : h.severity === 'warn' || h.severity === 'WARNING'
                            ? 'orange'
                            : 'default'
                      }
                    >
                      {h.severity || 'info'}
                    </Tag>
                    {h.code ? (
                      <Tag color={isPhase2HealthCode(h.code) ? 'purple' : undefined}>{h.code}</Tag>
                    ) : null}
                  </Space>
                  <div style={{ marginTop: 4 }}>{h.message || 'Issue'}</div>
                </div>
              );
            })
          )}
        </Space>
      </Drawer>
    </div>
  );
};
